/**
 * UI string catalogue — Telugu and English.
 *
 * Context: §16 open item 3 asked "Telugu only, or Telugu + English edition?"
 * with a default of "Telugu only; schema is i18n-ready". The client has since
 * decided both, so this is that decision implemented. Brief §49 ("do not
 * translate Telugu UI into English") is superseded by it — recorded in
 * docs/SOURCE_CONFLICTS.md rather than silently reversed.
 *
 * Telugu is the default and the fallback: an untranslated key renders Telugu,
 * never a raw key or an empty string. This is a Telugu-first product and an
 * English gap must degrade toward Telugu, not toward nothing.
 *
 * Deliberately dependency-free. The catalogue is small and typed, so a missing
 * or misspelled key is a compile error rather than a runtime blank.
 */

export const LANGUAGES = ['te', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<Language, string> = {
  te: 'తెలుగు',
  en: 'English',
};

/** Every translatable string in the reader UI. */
export const STRINGS = {
  // --- masthead / chrome ---------------------------------------------------
  'site.name': { te: 'టాప్ తెలుగు న్యూస్', en: 'Top Telugu News' },
  'site.tagline': { te: 'Top Telugu News', en: 'Top Telugu News' },
  'nav.home': { te: 'హోమ్', en: 'Home' },
  'nav.epaper': { te: 'ఈ-పేపర్', en: 'E-Paper' },
  'nav.search': { te: 'వెతకండి', en: 'Search' },
  'nav.menu': { te: 'విభాగాల మెనూ', en: 'Sections menu' },
  'nav.sections': { te: 'విభాగాలు', en: 'Sections' },
  'nav.signIn': { te: 'సైన్ ఇన్', en: 'Sign in' },
  'nav.edition': { te: 'ఎడిషన్', en: 'Edition' },
  'nav.editionAll': { te: 'ఎడిషన్: అన్నీ', en: 'Edition: All' },
  'nav.chooseEdition': { te: 'ఎడిషన్ ఎంచుకోండి', en: 'Choose edition' },

  // --- reader controls -----------------------------------------------------
  'reader.fontSize': { te: 'అక్షర పరిమాణం', en: 'Text size' },
  'reader.language': { te: 'భాష', en: 'Language' },
  'reader.listen': { te: 'వినండి', en: 'Listen' },
  'reader.listenTagline': { te: 'చదివే టైం లేదా? వినేయండి!', en: 'No time to read? Just listen!' },
  'reader.location': { te: 'మీ ప్రాంతం', en: 'Your area' },
  'reader.chooseLocation': { te: 'ప్రాంతం ఎంచుకోండి', en: 'Choose your area' },
  'reader.listenSoon': { te: 'టెక్స్ట్-టు-స్పీచ్ త్వరలో', en: 'Text-to-speech coming soon' },
  'reader.bookmark': { te: 'బుక్‌మార్క్', en: 'Bookmark' },
  'reader.bookmarkLogin': {
    te: 'బుక్‌మార్క్‌లకు లాగిన్ అవసరం',
    en: 'Sign in to bookmark',
  },
  'reader.share': { te: 'షేర్', en: 'Share' },
  'reader.authorPage': { te: 'రచయిత పేజీ', en: 'Author page' },
  'reader.readingTime': { te: 'నిమిషాల చదువు', en: 'min read' },
  'reader.desk': { te: 'టాప్ తెలుగు డెస్క్', en: 'Top Telugu Desk' },

  // --- home ----------------------------------------------------------------
  'home.breaking': { te: 'బ్రేకింగ్', en: 'Breaking' },
  'home.latest': { te: 'తాజా వార్తలు', en: 'Latest News' },
  'home.topStories': { te: 'ముఖ్య కథనాలు', en: 'Top stories' },
  'home.briefs': { te: 'క్లుప్తంగా', en: 'In Brief' },
  'home.seeAll': { te: 'అన్నీ చూడండి', en: 'See all' },
  'home.moreStories': { te: 'మరిన్ని కథనాలు', en: 'More stories' },
  'home.readEpaper': { te: 'ఈ-పేపర్ చదవండి', en: 'Read the e-paper' },
  'home.todaysPage': { te: 'నేటి పేజీ 1', en: "Today's page 1" },
  'home.advertisement': { te: 'ప్రకటన', en: 'Advertisement' },

  // --- article -------------------------------------------------------------
  'article.related': { te: 'సంబంధిత కథనాలు', en: 'Related stories' },
  'article.gallery': { te: 'ఫోటో గ్యాలరీ', en: 'Photo gallery' },
  'article.photoBy': { te: 'ఫోటో', en: 'Photo' },
  'article.source': { te: 'మూలం', en: 'Source' },
  'article.corrected': { te: 'సవరించబడింది', en: 'Corrected' },
  'article.editorNote': { te: 'ఎడిటర్ నోట్ · సవరణ', en: "Editor's note · Correction" },
  'article.exclusive': { te: 'ఎక్స్‌క్లూజివ్', en: 'Exclusive' },
  'article.aiDisclosure': {
    te: 'ఈ కథనం AI సహాయంతో రూపొందించబడి, ప్రచురణకు ముందు సీనియర్ ఎడిటర్ ద్వారా సమీక్షించబడింది.',
    en: 'This article was produced with AI assistance and reviewed by a senior editor before publication.',
  },
  'article.aiImage': {
    te: 'AI రూపొందించిన చిత్రం',
    en: 'AI-generated image',
  },
  /**
   * A library photo standing in for a picture of the event. The article page
   * prints it in the caption; cards and listings show no caption at all, which
   * is where most readers meet the photo — so `NewsImage` badges it there.
   */
  'article.representativeImage': {
    te: 'ప్రాతినిధ్య చిత్రం',
    en: 'Representative image',
  },
  /** Shown when a story has no English headline yet (§7.3 translation is Phase 7). */
  'article.teluguOnly': {
    te: 'ఈ కథనం తెలుగులో మాత్రమే అందుబాటులో ఉంది.',
    en: 'This story is available in Telugu only.',
  },
  'article.viewInTelugu': { te: 'తెలుగులో చదవండి', en: 'Read in Telugu' },

  // --- gallery -------------------------------------------------------------
  'gallery.enlarge': { te: 'పెద్దదిగా చూడండి', en: 'View larger' },
  'gallery.close': { te: 'మూసివేయండి', en: 'Close' },
  'gallery.previous': { te: 'మునుపటి ఫోటో', en: 'Previous photo' },
  'gallery.next': { te: 'తదుపరి ఫోటో', en: 'Next photo' },

  // --- states --------------------------------------------------------------
  'state.loading': { te: 'లోడ్ అవుతోంది…', en: 'Loading…' },
  'state.loadingNews': { te: 'వార్తలు లోడ్ అవుతున్నాయి', en: 'Loading news' },
  'state.loadingArticle': { te: 'కథనం లోడ్ అవుతోంది', en: 'Loading article' },
  'state.retry': { te: 'మళ్లీ ప్రయత్నించండి', en: 'Try again' },
  'state.newsFailed': { te: 'వార్తలు లోడ్ కాలేదు', en: 'Could not load the news' },
  'state.newsFailedBody': {
    te: 'వార్తలు చదవడంలో సమస్య వచ్చింది.',
    en: 'Something went wrong loading the news.',
  },
  'state.articleFailed': { te: 'కథనం లోడ్ కాలేదు', en: 'Could not load the article' },
  'state.articleNotFound': { te: 'కథనం కనిపించలేదు', en: 'Article not found' },
  'state.empty': { te: 'ఇంకా కథనాలు లేవు', en: 'No stories yet' },
  'state.emptyEdition': {
    te: 'ఈ ఎడిషన్‌లో ప్రస్తుతం ప్రచురించిన కథనాలు లేవు.',
    en: 'No published stories in this edition right now.',
  },
  'state.emptySection': {
    te: 'ఈ విభాగంలో ఇంకా కథనాలు లేవు.',
    en: 'No stories in this section yet.',
  },
  'state.goHome': { te: 'హోమ్‌కు వెళ్లండి', en: 'Go to the home page' },
  'state.photo': { te: 'ఫోటో', en: 'Photo' },
  'state.heroPhoto': { te: 'హీరో ఫోటో', en: 'Lead photo' },
  'state.noPermission': { te: 'ఈ పేజీకి మీకు అనుమతి లేదు', en: 'You do not have access to this page' },

  // --- time ----------------------------------------------------------------
  'time.now': { te: 'ఇప్పుడే', en: 'just now' },
  'time.minutesAgo': { te: 'నిమిషాల క్రితం', en: 'min ago' },
  'time.hoursAgo': { te: 'గం. క్రితం', en: 'hr ago' },
  'time.yesterday': { te: 'నిన్న', en: 'yesterday' },
  'time.daysAgo': { te: 'రోజుల క్రితం', en: 'days ago' },

  // --- footer / policy -----------------------------------------------------
  'footer.about': { te: 'మా గురించి', en: 'About us' },
  'footer.contact': { te: 'సంప్రదించండి', en: 'Contact' },
  'footer.editorial': { te: 'ఎడిటోరియల్ పాలసీ', en: 'Editorial policy' },
  'footer.corrections': { te: 'సవరణల విధానం', en: 'Corrections policy' },
  'footer.grievance': { te: 'గ్రీవెన్స్ అధికారి', en: 'Grievance officer' },
  'footer.privacy': { te: 'ప్రైవసీ', en: 'Privacy' },
  'footer.terms': { te: 'నిబంధనలు', en: 'Terms' },
  'footer.aiDisclosure': { te: 'AI వినియోగ ప్రకటన', en: 'AI usage disclosure' },
  'footer.ugcTerms': { te: 'పాఠకుల కథనాల నిబంధనలు', en: 'Reader content terms' },

  // --- generic UI chrome (primitives) ---------------------------------------
  'ui.close': { te: 'మూసివేయండి', en: 'Close' },
  'ui.cancel': { te: 'రద్దు చేయండి', en: 'Cancel' },
  'ui.confirm': { te: 'నిర్ధారించండి', en: 'Confirm' },
  'ui.delete': { te: 'తొలగించండి', en: 'Delete' },
  'ui.remove': { te: 'తీసివేయండి', en: 'Remove' },
  'ui.save': { te: 'సేవ్ చేయండి', en: 'Save' },
  'ui.saving': { te: 'సేవ్ అవుతోంది…', en: 'Saving…' },
  'ui.saved': { te: 'సేవ్ అయింది', en: 'Saved' },
  'ui.ok': { te: 'సరే', en: 'OK' },
  'ui.back': { te: 'వెనుకకు', en: 'Back' },
  'ui.next': { te: 'తదుపరి', en: 'Next' },
  'ui.previous': { te: 'మునుపటి', en: 'Previous' },
  'ui.more': { te: 'మరిన్ని', en: 'More' },
  'ui.loadMore': { te: 'మరిన్ని లోడ్ చేయండి', en: 'Load more' },
  'ui.showAll': { te: 'అన్నీ చూపించు', en: 'Show all' },
  'ui.search': { te: 'వెతకండి', en: 'Search' },
  'ui.clear': { te: 'క్లియర్', en: 'Clear' },
  'ui.select': { te: 'ఎంచుకోండి', en: 'Select' },
  'ui.selected': { te: 'ఎంచుకున్నవి', en: 'selected' },
  'ui.optional': { te: 'ఐచ్ఛికం', en: 'optional' },
  'ui.required': { te: 'తప్పనిసరి', en: 'required' },
  'ui.characters': { te: 'అక్షరాలు', en: 'characters' },
  'ui.copy': { te: 'కాపీ చేయండి', en: 'Copy' },
  'ui.copied': { te: 'కాపీ అయింది', en: 'Copied' },
  'ui.copyLink': { te: 'లింక్ కాపీ చేయండి', en: 'Copy link' },
  'ui.share': { te: 'షేర్ చేయండి', en: 'Share' },
  'ui.play': { te: 'ప్లే', en: 'Play' },
  'ui.pause': { te: 'పాజ్', en: 'Pause' },
  'ui.speed': { te: 'వేగం', en: 'Speed' },
  'ui.menu': { te: 'మెనూ', en: 'Menu' },
  'ui.openMenu': { te: 'మెనూ తెరవండి', en: 'Open menu' },
  'ui.closeMenu': { te: 'మెనూ మూసివేయండి', en: 'Close menu' },
  'ui.dismiss': { te: 'తొలగించు', en: 'Dismiss' },
  'ui.scrollLeft': { te: 'ఎడమకు స్క్రోల్', en: 'Scroll left' },
  'ui.scrollRight': { te: 'కుడికి స్క్రోల్', en: 'Scroll right' },
  'ui.edit': { te: 'సవరించండి', en: 'Edit' },
  'ui.add': { te: 'జోడించండి', en: 'Add' },
  'ui.apply': { te: 'వర్తింపజేయండి', en: 'Apply' },
  'ui.done': { te: 'పూర్తయింది', en: 'Done' },
  'ui.yes': { te: 'అవును', en: 'Yes' },
  'ui.no': { te: 'కాదు', en: 'No' },
  'ui.on': { te: 'ఆన్', en: 'On' },
  'ui.off': { te: 'ఆఫ్', en: 'Off' },
  'ui.chooseFiles': { te: 'ఫైళ్లు ఎంచుకోండి', en: 'Choose files' },
  'ui.dropFiles': { te: 'ఫైళ్లను ఇక్కడ వదలండి లేదా ఎంచుకోండి', en: 'Drop files here or choose' },
  'ui.uploading': { te: 'అప్‌లోడ్ అవుతోంది…', en: 'Uploading…' },
  'ui.pending': { te: 'ప్రాసెస్ అవుతోంది…', en: 'Working…' },
  'ui.readingProgress': { te: 'చదివిన భాగం', en: 'Reading progress' },
  'ui.skipToContent': { te: 'ప్రధాన కంటెంట్‌కు వెళ్లండి', en: 'Skip to content' },
  'ui.backToTop': { te: 'పైకి వెళ్లండి', en: 'Back to top' },
  'ui.settings': { te: 'సెట్టింగ్స్', en: 'Settings' },
  'ui.readerSettings': { te: 'రీడర్ సెట్టింగ్స్', en: 'Reader settings' },
  'ui.theme': { te: 'థీమ్', en: 'Theme' },
  'ui.themeSystem': { te: 'సిస్టమ్', en: 'System' },
  'ui.themeLight': { te: 'లైట్', en: 'Light' },
  'ui.themeDark': { te: 'డార్క్', en: 'Dark' },
  'ui.darkMode': { te: 'డార్క్ మోడ్', en: 'Dark mode' },
  'ui.notifications': { te: 'నోటిఫికేషన్లు', en: 'Notifications' },
  'ui.unread': { te: 'చదవనివి', en: 'unread' },
  'ui.markAllRead': { te: 'అన్నీ చదివినట్లు గుర్తించండి', en: 'Mark all as read' },
  'ui.today': { te: 'ఈరోజు', en: 'Today' },
  'ui.yesterday': { te: 'నిన్న', en: 'Yesterday' },
  'ui.earlier': { te: 'అంతకు ముందు', en: 'Earlier' },
  'ui.comments': { te: 'వ్యాఖ్యలు', en: 'Comments' },
  'ui.like': { te: 'ఇష్టం', en: 'Like' },
  'article.criticNote': { te: 'సంపాదకుల వ్యాఖ్య', en: "Editor's note" },
  'ui.liked': { te: 'ఇష్టపడ్డారు', en: 'Liked' },
  'ui.follow': { te: 'ఫాలో', en: 'Follow' },
  'ui.following': { te: 'ఫాలో అవుతున్నారు', en: 'Following' },
  'ui.signInToContinue': { te: 'కొనసాగించడానికి సైన్ ఇన్ చేయండి', en: 'Sign in to continue' },
  'ui.signInBody': {
    te: 'ఇష్టం, సేవ్, వ్యాఖ్యల కోసం మీ ఖాతాలోకి సైన్ ఇన్ చేయండి.',
    en: 'Sign in to like, save and comment.',
  },
  'ui.whatsapp': { te: 'వాట్సాప్', en: 'WhatsApp' },
  'ui.shareStripTitle': { te: 'ఈ కథనం నచ్చిందా? మిత్రులతో పంచుకోండి:', en: 'Liked this story? Share it with friends:' },
  'ui.copyLinkShort': { te: 'లింక్ కాపీ', en: 'Copy link' },
  'ui.shareCard': { te: 'కార్డ్‌గా షేర్', en: 'Share as card' },
  'ui.shareNative': { te: 'ఇతర యాప్‌లు…', en: 'Other apps…' },
  'ui.tickerPause': { te: 'టిక్కర్ ఆపండి', en: 'Pause ticker' },
  'ui.tickerPlay': { te: 'టిక్కర్ నడపండి', en: 'Play ticker' },
  'ui.sections': { te: 'విభాగాలు', en: 'Sections' },
  'ui.product': { te: 'ప్రొడక్ట్', en: 'Product' },
  'ui.compliance': { te: 'నిబంధనలు & విధానాలు', en: 'Policies' },
  'ui.liveBlog': { te: 'లైవ్ బ్లాగ్', en: 'Live blog' },
  'ui.photos': { te: 'ఫోటోలు', en: 'Photos' },
  'ui.videos': { te: 'వీడియోలు', en: 'Videos' },
  'ui.webStories': { te: 'వెబ్ స్టోరీస్', en: 'Web Stories' },
  'ui.live': { te: 'లైవ్', en: 'Live' },
  'ui.trending': { te: 'ట్రెండింగ్', en: 'Trending' },
  'ui.local': { te: 'లోకల్', en: 'Local' },
  'ui.video': { te: 'వీడియో', en: 'Video' },
  'ui.shorts': { te: 'షార్ట్స్', en: 'Shorts' },
  'ui.explore': { te: 'ఇంకా చూడండి', en: 'Explore' },
  'ui.readNext': { te: 'తర్వాత చదవండి', en: 'Read next' },
  'ui.forYou': { te: 'మీ కోసం', en: 'For you' },
  'ui.forYouHint': {
    te: 'మీ పఠనం, ఆసక్తులు, ప్రాంతం ఆధారంగా ఎంపిక.',
    en: 'Picked from your reading, interests and location.',
  },
  'ui.topTopics': { te: 'ప్రజలు మాట్లాడుకుంటున్న టాప్ 3 అంశాలు', en: 'Top 3 issues people are talking about' },
  'ui.editorSelected': { te: 'ఎడిటర్ ఎంపిక', en: 'Editor selected' },
  'ui.trendingNow': { te: 'ఇప్పుడు ట్రెండింగ్', en: 'Trending now' },
  'ui.aiAssisted': { te: 'AI సహాయంతో', en: 'AI-assisted' },
  'ui.breaking': { te: 'బ్రేకింగ్', en: 'Breaking' },
  'ui.pages': { te: 'పేజీలు', en: 'pages' },

  // --- e-paper promo -----------------------------------------------------
  'epaper.promoEyebrow': { te: 'నేటి టాప్ తెలుగు న్యూస్ · ఈ-పేపర్', en: "Today's Top Telugu News · E-Paper" },
  'epaper.promoTitle': { te: 'వార్తాపత్రికలా చదవండి. రేడియోలా వినండి.', en: 'Read it like a newspaper. Hear it like radio.' },
  'epaper.promoToday': { te: 'ఈ రోజు తెలుగు వార్తలు', en: "Today's Telugu news" },
  'epaper.read': { te: 'వార్తాపత్రికలా చదవండి', en: 'Read like a newspaper' },
  'epaper.listen': { te: 'రేడియోలా వినండి', en: 'Listen like radio' },
  'epaper.download': { te: 'నేటి ఎడిషన్ డౌన్‌లోడ్', en: "Download today's edition" },
  'epaper.createMine': { te: 'నా ఈ-పేపర్ సృష్టించండి', en: 'Create my E-Paper' },
  'epaper.myEpaper': { te: 'నా ఈ-పేపర్', en: 'My E-Paper' },
  'epaper.sharePage': { te: 'పేజీ షేర్ చేయండి', en: 'Share page' },
  'epaper.archive': { te: 'ఆర్కైవ్', en: 'Archive' },
  'epaper.page': { te: 'పేజీ', en: 'Page' },
  'epaper.zoomIn': { te: 'పెద్దదిగా', en: 'Zoom in' },
  'epaper.zoomOut': { te: 'చిన్నదిగా', en: 'Zoom out' },
  'epaper.radio': { te: 'రేడియో', en: 'Radio' },

  // --- states (additions) --------------------------------------------------
  'state.errorTitle': { te: 'ఏదో తప్పు జరిగింది', en: 'Something went wrong' },
  'state.errorBody': { te: 'దయచేసి కాసేపటి తర్వాత మళ్లీ ప్రయత్నించండి.', en: 'Please try again in a moment.' },
  'state.offline': { te: 'ఇంటర్నెట్ కనెక్షన్ లేదు', en: 'You are offline' },
  'state.notFound': { te: 'పేజీ కనిపించలేదు', en: 'Page not found' },
  'state.notFoundBody': { te: 'మీరు వెతుకుతున్న పేజీ లేదు లేదా తరలించబడింది.', en: 'The page you are looking for does not exist or has moved.' },
  'state.loadingPage': { te: 'పేజీ లోడ్ అవుతోంది', en: 'Loading page' },
  'state.emptyTitle': { te: 'ఇక్కడ ఇంకా ఏమీ లేదు', en: 'Nothing here yet' },
  'state.endOfList': { te: 'ఇంతే. అన్నీ చూశారు.', en: "That's all for now." },
  'state.noResults': { te: 'ఫలితాలు లేవు', en: 'No results' },
  'state.success': { te: 'విజయవంతం', en: 'Success' },
  'state.saved': { te: 'సేవ్ అయింది', en: 'Saved' },
  'state.deleted': { te: 'తొలగించబడింది', en: 'Deleted' },
  'state.reported': { te: 'నివేదించారు. ధన్యవాదాలు.', en: 'Reported. Thank you.' },
  'state.updated': { te: 'అప్‌డేట్ అయింది', en: 'Updated' },
  'state.unsavedChanges': { te: 'సేవ్ కాని మార్పులు ఉన్నాయి. వెళ్లిపోతారా?', en: 'You have unsaved changes. Leave anyway?' },
  'state.confirmDelete': { te: 'దీన్ని తొలగించాలా? ఈ చర్యను వెనక్కి తీసుకోలేరు.', en: 'Delete this? This cannot be undone.' },

  // --- page titles (useDocumentTitle) --------------------------------------
  'page.home': { te: 'హోమ్', en: 'Home' },
  'page.search': { te: 'వెతకండి', en: 'Search' },
  'page.trending': { te: 'ట్రెండింగ్', en: 'Trending' },
  'page.local': { te: 'లోకల్ వార్తలు', en: 'Local news' },
  'page.notifications': { te: 'నోటిఫికేషన్లు', en: 'Notifications' },
  'page.login': { te: 'సైన్ ఇన్', en: 'Sign in' },
  'page.register': { te: 'నమోదు', en: 'Register' },
  'page.forgotPassword': { te: 'పాస్‌వర్డ్ మర్చిపోయారా', en: 'Forgot password' },
  'page.resetPassword': { te: 'పాస్‌వర్డ్ రీసెట్', en: 'Reset password' },
  'page.verifyEmail': { te: 'ఇమెయిల్ నిర్ధారణ', en: 'Verify email' },
  'page.profile': { te: 'నా ఖాతా', en: 'My account' },
  'page.submit': { te: 'కథనం పంపండి', en: 'Submit a story' },
  'page.bookmarks': { te: 'సేవ్ చేసినవి', en: 'Saved' },
  'page.history': { te: 'చదివినవి', en: 'History' },
  'page.following': { te: 'ఫాలోయింగ్', en: 'Following' },
  'page.epaper': { te: 'ఈ-పేపర్', en: 'E-Paper' },
  'page.myEpaper': { te: 'నా ఈ-పేపర్', en: 'My E-Paper' },
  'page.bulletin': { te: 'బులెటిన్', en: 'Bulletin' },
  'page.contributor': { te: 'విలేకరిగా చేరండి', en: 'Become a contributor' },
  'page.topic': { te: 'అంశం', en: 'Topic' },
  'page.poll': { te: 'పోల్', en: 'Poll' },
  'page.live': { te: 'లైవ్', en: 'Live' },
  'page.district': { te: 'జిల్లా', en: 'District' },
  'page.mandal': { te: 'మండలం', en: 'Mandal' },
  'page.photos': { te: 'ఫోటోలు', en: 'Photos' },
  'page.videos': { te: 'వీడియోలు', en: 'Videos' },
  'page.shortNews': { te: 'షార్ట్ న్యూస్', en: 'Short news' },
  'page.webStories': { te: 'వెబ్ స్టోరీస్', en: 'Web stories' },
  'page.author': { te: 'రచయిత', en: 'Author' },
  'page.tag': { te: 'ట్యాగ్', en: 'Tag' },
  'page.about': { te: 'మా గురించి', en: 'About us' },
  'page.contact': { te: 'సంప్రదించండి', en: 'Contact' },
  'page.editorialPolicy': { te: 'ఎడిటోరియల్ పాలసీ', en: 'Editorial policy' },
  'page.corrections': { te: 'సవరణల విధానం', en: 'Corrections policy' },
  'page.grievance': { te: 'గ్రీవెన్స్ అధికారి', en: 'Grievance officer' },
  'page.privacy': { te: 'ప్రైవసీ', en: 'Privacy' },
  'page.terms': { te: 'నిబంధనలు', en: 'Terms' },
  'page.aiDisclosure': { te: 'AI వినియోగ ప్రకటన', en: 'AI usage disclosure' },
  'page.ugcTerms': { te: 'పాఠకుల కథనాల నిబంధనలు', en: 'Reader content terms' },
  'page.notFound': { te: 'పేజీ కనిపించలేదు', en: 'Page not found' },
  'page.qaStatus': { te: 'సిస్టమ్ స్థితి', en: 'System status' },
  'page.qaTelugu': { te: 'తెలుగు రెండర్ టెస్ట్', en: 'Telugu render test' },

  // --- admin (chrome + page titles) ----------------------------------------
  'admin.cms': { te: 'న్యూస్‌రూమ్ CMS', en: 'Newsroom CMS' },
  'admin.searchPlaceholder': { te: 'కథనాలు వెతకండి…', en: 'Search articles…' },
  'admin.signOut': { te: 'లాగ్ అవుట్', en: 'Sign out' },
  'admin.teluguTyping': {
    te: 'తెలుగు టైపింగ్ (ఇంగ్లీష్ అక్షరాలతో) — ఉదా. telugu → తెలుగు',
    en: 'Telugu typing (phonetic) — e.g. telugu → తెలుగు',
  },
  'admin.toggleNav': { te: 'నావిగేషన్ మార్చండి', en: 'Toggle navigation' },
  'admin.collapseNav': { te: 'సైడ్‌బార్ కుదించండి', en: 'Collapse sidebar' },
  'admin.expandNav': { te: 'సైడ్‌బార్ విస్తరించండి', en: 'Expand sidebar' },
  'admin.group.newsroom': { te: 'న్యూస్‌రూమ్', en: 'Newsroom' },
  'admin.group.review': { te: 'సమీక్ష', en: 'Review' },
  'admin.group.content': { te: 'కంటెంట్', en: 'Content' },
  'admin.group.audience': { te: 'పాఠకులు', en: 'Audience' },
  'admin.group.publishing': { te: 'ప్రచురణ', en: 'Publishing' },
  'admin.group.settings': { te: 'సెట్టింగ్స్', en: 'Settings' },
  'admin.page.dashboard': { te: 'డాష్‌బోర్డ్', en: 'Dashboard' },
  'admin.page.assistant': { te: 'సంజయ AI', en: 'Sanjaya AI' },
  'admin.page.articles': { te: 'కథనాలు', en: 'Articles' },
  'admin.page.newArticle': { te: 'కొత్త కథనం', en: 'New article' },
  'admin.page.shortNews': { te: 'షార్ట్ న్యూస్', en: 'Short news' },
  'admin.page.creative': { te: 'క్రియేటివ్ స్టూడియో', en: 'Creative studio' },
  'admin.page.crawlSettings': { te: 'క్రాల్ సెట్టింగ్స్', en: 'Crawl settings' },
  'admin.page.editArticle': { te: 'కథనం సవరించండి', en: 'Edit article' },
  'admin.page.review': { te: 'రివ్యూ క్యూ', en: 'Review queue' },
  'admin.page.pending': { te: 'పెండింగ్ కథనాలు', en: 'Pending articles' },
  'admin.page.ai': { te: 'AI సూచనలు', en: 'AI suggestions' },
  'admin.page.sources': { te: 'కంటెంట్ మూలాలు', en: 'Content sources' },
  'admin.page.moderation': { te: 'మోడరేషన్', en: 'Moderation' },
  'admin.page.kyc': { te: 'విలేకరి దరఖాస్తులు', en: 'Contributors' },
  'admin.page.trending': { te: 'ట్రెండింగ్', en: 'Trending' },
  'admin.page.pins': { te: 'పిన్‌లు', en: 'Pins' },
  'admin.page.notifications': { te: 'నోటిఫికేషన్లు', en: 'Notifications' },
  'admin.page.videos': { te: 'వీడియోలు', en: 'Videos' },
  'admin.page.epaper': { te: 'ఈ-పేపర్', en: 'E-Paper' },
  'admin.page.polls': { te: 'పోల్స్', en: 'Polls' },
  'admin.page.ads': { te: 'ప్రకటనలు', en: 'Ads' },
  'admin.page.homepage': { te: 'హోమ్ విభాగాలు', en: 'Homepage' },
  'admin.page.analytics': { te: 'విశ్లేషణలు', en: 'Analytics' },
  'admin.page.taxonomy': { te: 'వర్గీకరణ', en: 'Taxonomy' },
  'admin.page.media': { te: 'మీడియా', en: 'Media' },
  'admin.page.users': { te: 'వినియోగదారులు', en: 'Users' },
  'admin.page.roles': { te: 'పాత్రలు', en: 'Roles' },
  'admin.page.audit': { te: 'ఆడిట్', en: 'Audit' },
  'admin.page.voice': { te: 'వాయిస్', en: 'Voice' },
  'admin.page.bulletins': { te: 'బులెటిన్లు', en: 'Bulletins' },
  'admin.page.settings': { te: 'సెట్టింగ్స్', en: 'Settings' },
  'admin.page.login': { te: 'CMS లాగిన్', en: 'CMS sign in' },
  'admin.selfApprove': { te: 'మీ సొంత కథనాన్ని మీరు ఆమోదించలేరు', en: 'You cannot approve your own article' },
  'admin.wordCount': { te: 'పదాలు', en: 'words' },
  'admin.unsaved': { te: 'సేవ్ కాలేదు', en: 'Unsaved' },

  // --- W1 primitives (reported by builders) --------------------------------
  'ui.reload': { te: 'మళ్లీ లోడ్ చేయండి', en: 'Reload' },
  'ui.toastRegion': { te: 'సందేశాలు', en: 'Messages' },
  'reader.previewText': {
    te: 'తెలుగు వార్తలు మీకు నచ్చిన అక్షర పరిమాణంలో చదవండి. ఈ నమూనా వాక్యం మీ ఎంపికను చూపిస్తుంది.',
    en: 'Read Telugu news at the size you prefer. This sample sentence shows your choice.',
  },
  'ui.fontStepSmaller': { te: 'చిన్నది', en: 'Smaller' },
  'ui.fontStepDefault': { te: 'సాధారణం', en: 'Default' },
  'ui.fontStepLarger': { te: 'పెద్దది', en: 'Larger' },
  'ui.fontStepLargest': { te: 'అతి పెద్దది', en: 'Largest' },

  // --- §17 asking a provider to draw something -----------------------------
  // Chrome, not domain copy: the picker's own wording stays inline beside it.
  'ui.generate': { te: 'తయారు చేయండి', en: 'Generate' },
  'ui.generating': { te: 'తయారవుతోంది…', en: 'Generating…' },

  // --- the global audio player (components/player) ---------------------------
  'player.label': { te: 'ఆడియో ప్లేయర్', en: 'Audio player' },
  'player.nowPlaying': { te: 'ఇప్పుడు వినిపిస్తోంది', en: 'Now playing' },
  'player.open': { te: 'ప్లేయర్ తెరవండి', en: 'Open player' },
  'player.minimise': { te: 'ప్లేయర్ చిన్నదిగా చేయండి', en: 'Minimise player' },
  'player.stop': { te: 'ఆపి ప్లేయర్ మూసివేయండి', en: 'Stop and close the player' },
  'player.back15': { te: '15 సెకన్లు వెనక్కి', en: 'Back 15 seconds' },
  'player.forward15': { te: '15 సెకన్లు ముందుకు', en: 'Forward 15 seconds' },
  'player.position': { te: 'ఆడియో స్థానం', en: 'Audio position' },
  'player.upNext': { te: 'వరుసలో', en: 'Up next' },
  'player.readStory': { te: 'వార్త చదవండి', en: 'Read the story' },
  'player.onAir': { te: 'ప్రసారంలో', en: 'On air' },
  'player.offAir': { te: 'ప్రసారం కాలేదు', en: 'Not on air' },
  'player.buffering': { te: 'లోడ్ అవుతోంది…', en: 'Loading…' },
  'player.error': { te: 'ఆడియో ప్లే కాలేదు', en: 'The audio could not play' },
  'player.playAll': { te: 'అన్నీ వినండి', en: 'Play all' },
  'player.dial': { te: 'నేటి బులెటిన్ల డయల్', en: "Today's bulletin dial" },
  'player.kind.article': { te: 'వార్త', en: 'Story' },
  'player.kind.bulletin': { te: 'ఆడియో బులెటిన్', en: 'Audio bulletin' },
  'player.kind.epaper': { te: 'ఈ-పేపర్ రేడియో', en: 'E-paper radio' },
} as const satisfies Record<string, Record<Language, string>>;

export type StringKey = keyof typeof STRINGS;
